/**
 * Agrupación del menú del sidebar en secciones (capa frontend-only).
 *
 * El menú llega del backend (`GET /crm/staffs/me`) como ~22 items raíz PLANOS
 * ya filtrados por permisos. Las buenas prácticas de navegación recomiendan
 * <7 grupos de primer nivel con buena agrupación, así que aquí se mapea cada
 * item a una sección por su URL (el identificador más estable entre tenants:
 * los ids y nombres de menú pueden variar por marca en BD).
 *
 * Reglas:
 * - Una sección sin items (por permisos) NO renderiza su header.
 * - URLs no mapeadas caen en la sección "Otros" (fallback visible y funcional),
 *   con console.warn solo en DEV para detectarlas.
 * - "Inicio" queda suelto arriba, fuera de cualquier sección.
 *
 * Los renames (LABEL_OVERRIDES) solo aplican si el backend manda exactamente
 * el nombre `from`: un nombre personalizado por tenant en BD no se pisa.
 * Consolidar los renames en la tabla de menús de middleware-api los vuelve
 * no-ops sin tocar este archivo.
 */

export interface MenuSection {
  id: string
  label: string
}

/** Orden de render de las secciones en el sidebar. */
export const MENU_SECTIONS: MenuSection[] = [
  { id: "standalone", label: "" }, // Inicio — sin header
  { id: "clientes-lineas", label: "Clientes y Líneas" },
  { id: "ventas", label: "Ventas" },
  { id: "marketing", label: "Marketing" },
  { id: "finanzas", label: "Finanzas" },
  { id: "operaciones", label: "Operaciones" },
  { id: "administracion", label: "Administración" },
  { id: "otros", label: "Otros" }, // fallback para URLs no mapeadas
]

/**
 * URL raíz (normalizada, sin slash final) → id de sección.
 * Matching por igualdad o prefijo (`/clientes` cubre `/clientes/123`).
 */
export const URL_TO_SECTION: Record<string, string> = {
  "/": "standalone", // el backend puede mandar Inicio con url raíz
  "/inicio": "standalone",
  "/home": "standalone",

  "/clientes": "clientes-lineas",
  "/leads": "clientes-lineas",
  "/msisdn": "clientes-lineas",
  "/consulta-uf": "clientes-lineas",
  "/menu": "clientes-lineas", // alias legacy de consulta de línea
  "/portabilidades": "clientes-lineas",
  "/portabilidad": "clientes-lineas",

  "/ventas": "ventas",
  "/ofertas": "ventas",
  "/promociones": "ventas",
  "/promociones-altan": "ventas",
  "/ecommerce": "ventas",
  "/ecommerce-products": "ventas",

  "/banners": "marketing",
  "/sms-campaigns": "marketing",
  "/push-campaigns": "marketing",
  "/medallas": "marketing",
  "/sms-templates": "marketing",

  "/facturacion": "finanzas",
  "/liquidaciones": "finanzas",

  "/batch": "operaciones",
  "/preactivacion": "operaciones",
  "/esim-profiles": "operaciones",

  "/usuarios": "administracion",
  "/empresas": "administracion",
  "/datos-empresa": "administracion",
  "/familias": "administracion",
  // El sidebar lo oculta (useMenuItems), pero la pantalla de permisos sí lo pinta.
  "/marcas-blancas": "administracion",
  "/beneficios": "clientes-lineas",
}

/**
 * Renames de labels para mejorar el "information scent" (jerga → lenguaje de
 * negocio, inglés → español). Keyed por URL; el override SOLO aplica si el
 * nombre que manda el backend es uno de los `from` (los defaults sembrados en
 * las marcas): un nombre personalizado por tenant en BD no se pisa.
 *
 * La pantalla de permisos usa ESTOS mismos nombres: lo que el administrador
 * otorga se llama igual que lo que la persona ve en su menú.
 */
export const LABEL_OVERRIDES: Record<string, { from: string[]; to: string }> = {
  // "Consulta de perfil de UF" es el nombre real sembrado en las 4 marcas de back; el override
  // viejo esperaba "Consulta UF" y nunca aplicaba.
  "/consulta-uf": { from: ["Consulta UF", "Consulta de perfil de UF"], to: "Consulta de Línea" },
  "/portabilidad": { from: ["Portabilidad, Activacion y Recarga"], to: "Operaciones" },
  "/batch": { from: ["Operaciones Batch"], to: "Batch" },
  "/msisdn": { from: ["MSISDN", "Msisdn"], to: "Líneas (MSISDN)" },
  "/datos-empresa": { from: ["Datos Empresa", "Datos de Empresa"], to: "Configuración de Empresa" },
  "/ecommerce": { from: ["E-Commerce"], to: "Pedidos E-Commerce" },
  "/esim-profiles": { from: ["eSIM Profiles", "eSIM Perfiles"], to: "Perfiles eSIM" },
  "/sms-campaigns": { from: ["SMS Campaigns"], to: "Campañas SMS" },
  "/sms-templates": { from: ["SMS Templates"], to: "Plantillas SMS" },
}

/** Normaliza una URL de menú para hacer matching estable. */
export function normalizeMenuUrl(url: string): string {
  const trimmed = (url || "").trim().replace(/^#/, "")
  if (!trimmed) return ""
  if (trimmed === "/") return "/" // raíz es URL válida (Inicio)
  const withSlash = trimmed.startsWith("/") ? trimmed : `/${trimmed}`
  return withSlash.replace(/\/+$/, "").toLowerCase()
}

/** Resuelve la sección de una URL de menú (igualdad o prefijo); null si no mapea. */
export function sectionForUrl(url: string): string | null {
  const normalized = normalizeMenuUrl(url)
  if (!normalized) return null
  if (URL_TO_SECTION[normalized]) return URL_TO_SECTION[normalized]
  // Prefijo: /clientes/algo → /clientes
  const match = Object.keys(URL_TO_SECTION).find((root) =>
    normalized.startsWith(`${root}/`)
  )
  return match ? URL_TO_SECTION[match] : null
}

/** Aplica el rename de label si el backend manda uno de los nombres esperados. */
export function overrideLabel(url: string, backendName: string): string {
  const override = LABEL_OVERRIDES[normalizeMenuUrl(url)]
  if (override && override.from.includes(backendName.trim())) return override.to
  return backendName
}
